// IM-EXT-007 — Concorrentes conhecidos: buscar → identificar → selecionar → enriquecer → revisar → classificar.
// O usuário digita só o nome e escolhe a empresa certa; o InteliMove confere, pesquisa e compara com o contexto da
// empresa. Tudo o que a pesquisa traz fica pendente de decisão humana; o que a pessoa informou nunca é sobrescrito.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type {
  CompanyContextDto, CompetitorAnalysisItem, CompetitorLookupDto, CompetitorCandidateDto, CompetitorDetailDto, CompetitorExistingDto, CompetitorField, CompetitorIdentityDto, CompetitorStatus, ContextEvidenceDto,
  LegalEntityDto, LegalEntityRole,
} from '@intelimove/contracts';
import { ApiError } from '../../api/client';
import { EmptyState } from '../../components/States';
import { useToast } from '../../components/Toast';
import { StatusPill } from '../../components/Widgets';
import {
  competitorAdapter, COMPETITOR_STATUS_TXT, FIELD_STATE_TXT, LEVEL_TONE, OVERLAP_TXT, PROVENANCE_TXT, RELATION_TXT, researchState, RUN_STEPS, SUGGESTION_TXT, TRIGGER_TXT,
  type CompetitorFull, type CompetitorSelection,
} from '../../data/competitors';
import { NATURE_TONE, NATURE_TXT } from '../../data/context';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { newId } from '../../lib/id';

type Ctx = CompanyContextDto;
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const fmtCnpj = (v: string | null) => (v && /^\d{14}$/.test(v) ? v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : v);
const place = (x: { city?: string | null; region?: string | null; country?: string | null }) => [[x.city, x.region].filter(Boolean).join('/'), x.country && x.country !== 'BR' ? x.country : x.country === 'BR' && !x.city ? 'Brasil' : null].filter(Boolean).join(' · ');
const safeHref = (u: string | null | undefined) => (u && /^https?:\/\//i.test(u) ? u : undefined);

function useBusy() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const run = async <T,>(fn: () => Promise<T>, ok?: string): Promise<T | null> => {
    setBusy(true);
    try { const r = await fn(); if (ok) toast(ok); return r; } catch (e) { toast((e as Error).message, 'error'); return null; } finally { setBusy(false); }
  };
  return { busy, run };
}

// ── Lista ───────────────────────────────────────────────────────────────────────────────────────────────────────────
export function Competitors({ ctx, canEdit }: { ctx: Ctx; canEdit: boolean }) {
  const [params, setParams] = useSearchParams();
  const openId = params.get('c');
  const [items, setItems] = useState<CompetitorDetailDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const load = useCallback(() => competitorAdapter.list().then((r) => { setItems(r.items); setError(null); }).catch((e: Error) => setError(e.message)), []);
  useEffect(() => { void load(); }, [load]);
  // Pesquisa em andamento: a lista se atualiza sozinha; dá para sair da página e voltar (o estado está no servidor).
  const active = items?.some((x) => researchState(x).active) ?? false;
  useEffect(() => { if (!active) return; const t = setInterval(load, 3000); return () => clearInterval(t); }, [active, load]);
  const open = (id: string | null) => { const p = new URLSearchParams(params); if (id) p.set('c', id); else p.delete('c'); setParams(p, { replace: false }); };
  const visible = (items ?? []).filter((x) => showArchived || x.status !== 'ARCHIVED');
  const archived = (items ?? []).filter((x) => x.status === 'ARCHIVED').length;
  return (
    <section aria-labelledby="cmp-title">
      <div className="card-head">
        <div><h2 id="cmp-title">Concorrentes conhecidos</h2><p className="card-sub">Digite só o nome: o InteliMove encontra a empresa, confere a identidade, pesquisa e compara com a sua. Nada é confirmado sem você.</p></div>
        {canEdit && !adding && <button className="cta" onClick={() => setAdding(true)}>Adicionar</button>}
      </div>
      {adding && <AddCompetitor ctx={ctx} onCancel={() => setAdding(false)} onDone={(id) => { setAdding(false); void load(); open(id); }} onOpenExisting={(id) => { setAdding(false); open(id); }} known={items ?? []} />}
      {error && <p role="alert" className="form-error">{error}</p>}
      {items && visible.length === 0 && !adding && <EmptyState title="Nenhum concorrente cadastrado">{canEdit ? 'Use “Adicionar” e digite o nome da empresa.' : 'Um administrador pode adicionar.'}</EmptyState>}
      <ul className="entity-list cmp-list">
        {visible.map((x) => {
          const [label, tone] = COMPETITOR_STATUS_TXT[x.status] ?? [x.status, 'gray'];
          const st = researchState(x);
          const isOpen = openId === x.id;
          return (
            <li key={x.id} className={`card cmp-item${isOpen ? ' open' : ''}`}>
              <div className="card-head cmp-row">
                <div>
                  <h3>{x.name}{x.tradeName && x.tradeName !== x.name ? <span className="cell-sub inline"> · {x.tradeName}</span> : null}</h3>
                  <p className="card-sub">{[x.domain, place(x)].filter(Boolean).join(' · ') || 'sem site'}{x.suggestedClassification && x.reviewPending ? ` · sugestão da pesquisa: ${SUGGESTION_TXT[x.suggestedClassification]}` : ''}</p>
                </div>
                <div className="cmp-badges">
                  <StatusPill tone={tone}>{label}</StatusPill>
                  <span className={`tag ${st.tone}`} aria-live="polite">{st.active && <i className="spin" aria-hidden />}{st.label}</span>
                  <button className="small-action" aria-expanded={isOpen} onClick={() => open(isOpen ? null : x.id)}>{isOpen ? 'Fechar' : 'Abrir'}</button>
                </div>
              </div>
              {isOpen && <CompetitorReview id={x.id} ctx={ctx} canEdit={canEdit} onChanged={load} known={items ?? []} />}
            </li>
          );
        })}
      </ul>
      {archived > 0 && <button className="link-btn" onClick={() => setShowArchived((v) => !v)}>{showArchived ? 'Ocultar arquivados' : `Mostrar arquivados (${archived})`}</button>}
    </section>
  );
}

// ── Adicionar: busca, seleção, conferência ──────────────────────────────────────────────────────────────────────────
type Step = { kind: 'search' } | { kind: 'manual' } | { kind: 'confirm'; sel: CompetitorSelection; cand: CompetitorCandidateDto | null };

/** Mesma busca assistida (IM-EXT-007-C01) para concorrentes e empresas-alvo; muda só o destino do cadastro (IM-EXT-008). */
export interface CompanyFinder {
  title: string; cta: string;
  /** O destino guarda a decisão sobre as outras entidades jurídicas achadas na conferência (empresas-alvo). */
  entityRoles?: boolean;
  lookup: (q: string, signal: AbortSignal, retry: boolean) => Promise<CompetitorLookupDto>;
  identify: (sel: CompetitorSelection, signal: AbortSignal) => Promise<CompetitorIdentityDto & { competitor?: { id: string; name: string } | null }>;
  create: (sel: CompetitorSelection & { allowCompetitor?: boolean; entities?: Array<{ cnpj: string; role: LegalEntityRole }> }, key: string) => Promise<{ id: string }>;
  statusLabel: (status: string) => string;
}
export const COMPETITOR_FINDER: CompanyFinder = {
  title: 'Adicionar concorrente', cta: 'Analisar esta empresa',
  lookup: (q, signal, retry) => competitorAdapter.lookup(q, signal, retry), identify: (sel, signal) => competitorAdapter.identify(sel, signal),
  create: (sel, key) => competitorAdapter.create(sel, key), statusLabel: (st) => (COMPETITOR_STATUS_TXT[st] ?? [st])[0],
};

export function AddCompetitor({ ctx: _ctx, onCancel, onDone, onOpenExisting, known: _known, switchId, finder = COMPETITOR_FINDER, initialQuery = '' }: {
  ctx?: Ctx; onCancel: () => void; onDone: (id: string) => void; onOpenExisting: (id: string) => void; known: Array<{ id: string }>; switchId?: string; finder?: CompanyFinder;
  /** IM-EXT-011: busca já preenchida (ex.: "Adicionar como empresa-alvo" a partir de um sinal). */
  initialQuery?: string;
}) {
  const [q, setQ] = useState(initialQuery);
  const [step, setStep] = useState<Step>({ kind: 'search' });
  const [nonce, setNonce] = useState(0);
  const fetcher = useCallback((text: string, signal: AbortSignal) => finder.lookup(text, signal, nonce > 0), [nonce, finder]);
  const search = useDebouncedSearch(step.kind === 'search' ? q : '', fetcher, { delayMs: 500, minChars: 3, nonce, shouldCache: (d) => d.status === 'COMPLETE' && d.items.length > 0 });
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  useEffect(() => { if (step.kind === 'search') input.current?.focus(); }, [step.kind]);
  const data = search.kind === 'ready' ? search.data : search.kind === 'loading' ? search.previous : null;
  const items = data?.items ?? [];
  const pick = (c: CompetitorCandidateDto) => setStep({ kind: 'confirm', cand: c, sel: { name: c.tradeName ?? c.legalName ?? q.trim(), website: c.website, taxId: c.taxId, legalName: c.legalName } });
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    const opts = [...(list.current?.querySelectorAll<HTMLButtonElement>('button[data-cand]') ?? [])];
    const i = opts.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); (opts[i + 1] ?? opts[0])?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); if (i <= 0) input.current?.focus(); else opts[i - 1]?.focus(); }
    else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };
  // Nenhuma correspondência ≠ pesquisa parcial ≠ fonte indisponível ≠ erro (IM-EXT-007-C01).
  const status = search.kind === 'short' ? 'Digite pelo menos 3 letras.' : search.kind === 'loading' ? 'Buscando empresas em fontes públicas…' : search.kind === 'error' ? 'A busca falhou agora. Tente novamente.'
    : search.kind === 'ready' ? (items.length ? `${items.length} empresa${items.length > 1 ? 's' : ''} encontrada${items.length > 1 ? 's' : ''}. Escolha a correta.`
      : data?.status === 'UNAVAILABLE' ? 'A busca pública está indisponível agora.' : 'Nenhuma empresa encontrada com esse nome.') : '';
  const canRetry = search.kind === 'error' || (search.kind === 'ready' && data?.status !== 'COMPLETE');
  return (
    <div className="card cmp-add" onKeyDown={onKey}>
      <h3>{switchId ? 'Trocar empresa' : finder.title}</h3>
      {step.kind === 'search' && (
        <>
          <p className="card-sub">Digite o nome da empresa que deseja analisar.</p>
          <div className="cmp-search">
            <label htmlFor="cmp-q" className="sr-only">Buscar empresa</label>
            <input id="cmp-q" ref={input} type="search" placeholder="Buscar empresa" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" maxLength={120}
              aria-describedby="cmp-q-status" aria-controls="cmp-results" aria-label="Buscar empresa" />
            {search.kind === 'loading' && <i className="spin" aria-hidden />}
          </div>
          <p id="cmp-q-status" className="cell-sub wide" role="status" aria-live="polite">{status}</p>
          {data?.limitation && <p className={data.status === 'COMPLETE' ? 'cell-sub merge' : 'note-amber'}>{data.limitation}</p>}
          {canRetry && <button type="button" className="small-action" onClick={() => setNonce((n) => n + 1)}>Tentar novamente</button>}
          {data?.ambiguous && <p className="note-amber">Há empresas com nomes parecidos. Confira o site, a cidade e a razão social antes de escolher.</p>}
          {items.length > 0 && (
            <ul id="cmp-results" ref={list} className="cand-list" aria-label="Empresas encontradas">
              {items.map((c, i) => (
                <Fragment key={c.key}>
                  {DEMOTED.has(c.category ?? '') && !DEMOTED.has(items[i - 1]?.category ?? '') && i > 0 && <li className="cand-sep" role="presentation">Outros resultados, rebaixados — cada um mostra o motivo</li>}
                  <Candidate c={c} onPick={() => pick(c)} onOpen={onOpenExisting} />
                </Fragment>
              ))}
            </ul>
          )}
          <div className="form-actions spread">
            <button type="button" className="link-btn" onClick={() => setStep({ kind: 'manual' })}>Não encontrei a empresa</button>
            <button type="button" className="secondary" onClick={onCancel}>Cancelar</button>
          </div>
        </>
      )}
      {step.kind === 'manual' && <ManualEntry initialName={q} onBack={() => setStep({ kind: 'search' })} onCancel={onCancel} onNext={(sel) => setStep({ kind: 'confirm', sel, cand: null })} />}
      {step.kind === 'confirm' && <ConfirmSelection key={`${step.sel.taxId ?? ''}|${step.sel.website ?? ''}`} finder={finder} sel={step.sel} cand={step.cand} switchId={switchId} onBack={() => setStep({ kind: 'search' })} onCancel={onCancel} onDone={onDone} onOpenExisting={onOpenExisting}
        onReselect={(e) => setStep({ kind: 'confirm', cand: step.cand, sel: { ...step.sel, name: step.sel.name, taxId: e.cnpj, legalName: e.legalName } })} />}
    </div>
  );
}

function ConfidenceTag({ c }: { c: 'ALTA' | 'MEDIA' | 'BAIXA' }) {
  const t = { ALTA: ['Identificação forte', 'mint'], MEDIA: ['Identificação provável', 'amber'], BAIXA: ['Identificação fraca', 'critical'] }[c];
  return <span className={`tag ${t[1]}`}>{t[0]}</span>;
}

// IM-EXT-008-C01: papel de cada candidato na lista; nenhum é apagado — os rebaixados descem e dizem por quê.
const CATEGORY: Record<string, [string, string]> = {
  PRINCIPAL: ['Correspondência principal', 'mint'], POSSIVEL: ['Possível correspondência', 'amber'], RELACIONADA: ['Empresa relacionada', 'petrol'],
  GRUPO: ['Entidade do mesmo grupo', 'petrol'], ESTRANGEIRA: ['Entidade estrangeira', 'gray'], IRREGULAR: ['Empresa baixada ou irregular', 'critical'], SITE_NAO_CONFIRMADO: ['Site não confirmado', 'gray'],
};
const DEMOTED = new Set(['GRUPO', 'ESTRANGEIRA', 'IRREGULAR', 'SITE_NAO_CONFIRMADO']);

function Candidate({ c, onPick, onOpen }: { c: CompetitorCandidateDto; onPick: () => void; onOpen: (id: string) => void }) {
  const title = c.tradeName ?? c.legalName ?? c.domain ?? 'Empresa';
  const cat = c.category ? CATEGORY[c.category] : null;
  const divergences = c.divergences.filter((d) => d !== c.categoryReason);
  return (
    <li className="cand">
      <button type="button" data-cand className="cand-main" onClick={c.existing ? () => onOpen(c.existing!.id) : onPick} aria-label={`${c.existing ? 'Abrir registro existente de' : 'Selecionar'} ${title}${c.domain ? `, ${c.domain}` : ''}${c.city ? `, ${c.city}` : ''}`}>
        <span className="cand-name">{title}</span>
        {c.legalName && c.legalName !== title && <span className="cand-line">{c.legalName}</span>}
        <span className="cand-line">{[c.domain, place(c)].filter(Boolean).join(' · ') || 'site não identificado'}</span>
        {(c.description || c.segment) && <span className="cand-line muted">{c.description ?? c.segment}</span>}
        {c.taxId && <span className="cand-line small">CNPJ {fmtCnpj(c.taxId)}{c.registrationStatus ? ` · ${c.registrationStatus.toLowerCase()}` : ''}</span>}
        {c.categoryReason && <span className={`cand-line small${DEMOTED.has(c.category ?? '') ? ' warn' : ''}`}>{c.categoryReason}</span>}
        {divergences.length > 0 && <span className="cand-line warn">{divergences.join(' · ')}</span>}
        {!c.existing && (!c.taxId || c.confidence === 'BAIXA') && <span className="cand-line small">Identificação ainda não confirmada — o InteliMove confere ao selecionar.</span>}
      </button>
      <span className="cand-side">{c.existing ? <span className="tag gray">Já cadastrada</span> : cat ? <span className={`tag ${cat[1]}`}>{cat[0]}</span> : !c.taxId || c.confidence === 'BAIXA' ? <span className="tag amber">Possível correspondência</span> : <ConfidenceTag c={c.confidence} />}</span>
    </li>
  );
}

function ManualEntry({ initialName, onBack, onCancel, onNext }: { initialName: string; onBack: () => void; onCancel: () => void; onNext: (s: CompetitorSelection) => void }) {
  const [name, setName] = useState(initialName.trim());
  const [site, setSite] = useState('');
  const submit = (e: FormEvent) => { e.preventDefault(); if (name.trim().length >= 2) onNext({ name: name.trim(), website: site.trim() || null, manual: true }); };
  return (
    <form onSubmit={submit}>
      <p className="card-sub">Informe só o nome e, se souber, o site. O restante o InteliMove pesquisa.</p>
      <div className="form-grid">
        <div className="field"><label htmlFor="cmp-mname">Nome da empresa</label><input id="cmp-mname" required minLength={2} maxLength={160} value={name} onChange={(e) => setName(e.target.value)} autoFocus /></div>
        <div className="field"><label htmlFor="cmp-msite">Site ou domínio (opcional)</label><input id="cmp-msite" maxLength={500} placeholder="empresa.com.br" value={site} onChange={(e) => setSite(e.target.value)} /></div>
      </div>
      {!site.trim() && <p className="cell-sub wide">Sem o site, a empresa entra como hipótese e o InteliMove tenta descobrir o site pelo nome.</p>}
      <div className="form-actions spread">
        <button type="button" className="link-btn" onClick={onBack}>Voltar à busca</button>
        <span className="btn-row"><button type="button" className="secondary" onClick={onCancel}>Cancelar</button><button className="cta" disabled={name.trim().length < 2}>Continuar</button></span>
      </div>
    </form>
  );
}

function Existing({ e, onOpen, extra, statusLabel = (st) => (COMPETITOR_STATUS_TXT[st] ?? [st])[0] }: { e: CompetitorExistingDto; onOpen: (id: string) => void; extra?: ReactNode; statusLabel?: (st: string) => string }) {
  const label = statusLabel(e.status);
  const by = { DOMAIN: 'mesmo site', TAX_ID: 'mesmo CNPJ', LEGAL_NAME: 'mesma razão social', NAME: 'mesmo nome' }[e.matchedBy];
  return (
    <div className="note-amber" role="alert">
      <b>Esta empresa já está cadastrada</b> como “{e.name}” ({label.toLowerCase()}, {by}). Nada foi criado.
      {e.status === 'ARCHIVED' ? ' Abra o registro para restaurá-lo.' : ' Abra o registro para revisar ou refazer a pesquisa.'}
      <div className="btn-row"><button type="button" className="small-action" onClick={() => onOpen(e.id)}>Abrir registro existente</button>{extra}</div>
    </div>
  );
}

/**
 * IM-EXT-008-C01 — mais de uma entidade jurídica para a marca (CNPJ de outra empresa do grupo, instituição de pagamento,
 * matriz/filial). A selecionada continua a principal; para cada outra a pessoa decide: manter a selecionada (não
 * acompanhar esta), trocar a principal, marcar como relacionada ou deixar pendente. Nada é misturado nem bloqueado.
 */
export function EntityChoices({ entities, roles, onRole, onSwitch, canSwitch = true }: {
  entities: LegalEntityDto[]; roles?: Record<string, LegalEntityRole>; onRole?: (cnpj: string, role: LegalEntityRole) => void; onSwitch?: (e: LegalEntityDto) => void; canSwitch?: boolean;
}) {
  const decision = entities.some((e) => (roles?.[e.cnpj] ?? e.role) === 'PENDENTE' && ['GRUPO', 'FINANCEIRA', 'DIVERGENTE'].includes(e.relation));
  return (
    <div className={decision ? 'note-amber entity-box' : 'entity-box'} role={decision ? 'alert' : undefined}>
      <b>{decision ? 'Encontramos mais de uma entidade jurídica relacionada a esta marca. Confirme qual empresa deseja acompanhar.' : 'Entidades jurídicas relacionadas'}</b>
      <ul className="entity-list-mini">
        {entities.map((e) => {
          const role = roles?.[e.cnpj] ?? e.role;
          return (
            <li key={e.cnpj} className="entity-item">
              <div>
                <span className="cand-name">{e.legalName ?? 'Razão social não consultada'}</span>{e.tradeName && <span className="cell-sub inline"> · {e.tradeName}</span>}
                <span className="cand-line small">CNPJ {fmtCnpj(e.cnpj)}{e.status ? ` · ${e.status.toLowerCase()}` : ''}{e.city ? ` · ${[e.city, e.uf].filter(Boolean).join('/')}` : ''}{e.domain ? ` · ${e.domain}` : ''}</span>
                <span className="cand-line small"><b>{e.relationLabel}</b>{e.evidence ? ` — ${e.evidence}` : ''}</span>
              </div>
              {role === 'PRINCIPAL' ? <span className="tag mint">Entidade principal</span> : (
                <span className="entity-actions">
                  {onRole && (
                    <label className="sr-only" htmlFor={`ent-${e.cnpj}`}>Decisão sobre {e.legalName ?? e.cnpj}</label>
                  )}
                  {onRole && (
                    <select id={`ent-${e.cnpj}`} value={role} onChange={(ev) => onRole(e.cnpj, ev.target.value as LegalEntityRole)}>
                      <option value="DESCARTADA">Manter a entidade selecionada</option>
                      <option value="RELACIONADA">Marcar como entidade relacionada</option>
                      <option value="PENDENTE">Deixar pendente de confirmação</option>
                    </select>
                  )}
                  {onSwitch && canSwitch && <button type="button" className="small-action" onClick={() => onSwitch(e)}>Trocar entidade principal</button>}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ConfirmSelection({ finder, sel, cand, switchId, onBack, onCancel, onDone, onOpenExisting, onReselect }: {
  finder: CompanyFinder; sel: CompetitorSelection; cand: CompetitorCandidateDto | null; switchId?: string; onBack: () => void; onCancel: () => void; onDone: (id: string) => void; onOpenExisting: (id: string) => void;
  onReselect?: (e: LegalEntityDto) => void;
}) {
  const [roles, setRoles] = useState<Record<string, LegalEntityRole>>({});
  const [id, setId] = useState<Awaited<ReturnType<CompanyFinder['identify']>> | null>(null);
  const [alsoTarget, setAlsoTarget] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dup, setDup] = useState<CompetitorExistingDto | null>(null);
  const { busy, run } = useBusy();
  const key = useMemo(() => `ui-${newId()}`, []);
  useEffect(() => {
    const ctrl = new AbortController();
    finder.identify(sel, ctrl.signal).then((r) => { setId(r); if (r.existing && r.existing.id !== switchId) setDup(r.existing); }).catch((e: Error) => { if (!ctrl.signal.aborted) setErr(e.message); });
    return () => ctrl.abort();
  }, [sel, switchId, finder]);
  const analyze = async () => {
    try {
      const entities = finder.entityRoles ? (id?.relatedEntities ?? []).filter((e) => e.role !== 'PRINCIPAL').map((e) => ({ cnpj: e.cnpj, role: (roles[e.cnpj] ?? e.role) as LegalEntityRole })).filter((e) => e.role !== 'PRINCIPAL') : [];
      const body = { ...sel, ...(id?.competitor ? { allowCompetitor: alsoTarget } : {}), ...(entities.length ? { entities } : {}) };
      const r = await (switchId ? competitorAdapter.switch(switchId, sel, key) : finder.create(body, key));
      onDone(r.id);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'duplicate') setDup(((e as unknown as { details?: { existing?: CompetitorExistingDto } }).details?.existing) ?? id?.existing ?? null);
      setErr((e as Error).message);
    }
  };
  if (err && !id) return (<><p role="alert" className="form-error">Não foi possível conferir a empresa: {err}</p><div className="form-actions"><button className="secondary" onClick={onBack}>Trocar empresa</button><button className="secondary" onClick={onCancel}>Cancelar</button></div></>);
  if (!id) return <p className="cell-sub wide" role="status"><i className="spin" aria-hidden /> Conferindo a empresa: site, nome, CNPJ e registro oficial…</p>;
  return (
    <div className="cmp-confirm">
      <div className="cmp-summary">
        <div>
          <h4>{id.tradeName ?? id.name}</h4>
          {id.legalName && <p className="cell-sub">{id.legalName}{id.taxId ? ` · CNPJ ${fmtCnpj(id.taxId)}` : ''}</p>}
          <p className="cell-sub">{safeHref(id.website) ? <a href={id.website!} target="_blank" rel="noopener noreferrer">{id.domain}</a> : 'site não identificado'}{place(id) ? ` · ${place(id)}` : ''}</p>
          {id.description && <p className="cmp-desc">{id.description}</p>}
        </div>
        <ConfidenceTag c={id.confidence} />
      </div>
      {id.signals.length > 0 && (
        <ul className="signals" aria-label="Sinais conferidos">
          {id.signals.map((s) => <li key={s.key} className={`sig ${s.state.toLowerCase()}`}><b>{s.state === 'CONFIRMA' ? '✓' : s.state === 'DIVERGE' ? '✗' : '–'} {s.label}:</b> {s.detail}</li>)}
        </ul>
      )}
      {id.divergences.length > 0 && <ul className="notes">{id.divergences.filter((d) => !d.startsWith('Encontramos mais de uma entidade')).map((d) => <li key={d}>{d}</li>)}</ul>}
      {(id.relatedEntities ?? []).length > 0 && (
        <EntityChoices entities={id.relatedEntities!} roles={roles} onRole={finder.entityRoles ? (cnpj, role) => setRoles((r) => ({ ...r, [cnpj]: role })) : undefined}
          onSwitch={onReselect && !switchId ? onReselect : undefined} />
      )}
      {id.note && <p className="note-amber">{id.note}</p>}
      {cand && cand.sources.length > 0 && <p className="cell-sub">Encontrada em: {cand.sources.map((s) => s.title).join(' · ')}</p>}
      {id.competitor && !dup && (
        <div className="note-amber" role="alert">
          <b>Esta empresa está cadastrada como concorrente</b> (“{id.competitor.name}”). Uma empresa pode ser concorrente e também possível cliente.
          <label className="check-line"><input type="checkbox" checked={alsoTarget} onChange={(e) => setAlsoTarget(e.target.checked)} /> Confirmo que ela também é uma empresa-alvo</label>
        </div>
      )}
      {dup ? <Existing e={dup} onOpen={onOpenExisting} statusLabel={finder.statusLabel} /> : err ? <p role="alert" className="form-error">{err}</p> : null}
      <div className="form-actions spread">
        <button type="button" className="link-btn" onClick={onBack}>Trocar empresa</button>
        <span className="btn-row">
          <button type="button" className="secondary" onClick={onCancel}>Cancelar</button>
          {!dup && <button type="button" className="cta" disabled={busy || (!!id.competitor && !alsoTarget)} onClick={() => run(analyze)}>{switchId ? 'Analisar esta empresa no lugar' : finder.cta}</button>}
        </span>
      </div>
      <p className="cell-sub wide">A análise roda em segundo plano (alguns minutos). Você pode sair desta página e voltar depois.</p>
    </div>
  );
}

// ── Revisão ─────────────────────────────────────────────────────────────────────────────────────────────────────────
function Provenance({ c, field, canEdit, onDecide, busy }: { c: CompetitorDetailDto; field: CompetitorField; canEdit: boolean; onDecide: (f: CompetitorField, d: 'CONFIRM' | 'REJECT') => void; busy: boolean }) {
  const o = c.fieldOrigins[field];
  if (!o) return null;
  const [st, tone] = FIELD_STATE_TXT[o.state] ?? [o.state, 'gray'];
  return (
    <span className="prov">
      <span className={`tag ${o.origin === 'USER' ? 'mint' : o.origin === 'AI' ? 'amber' : 'petrol'}`}>{PROVENANCE_TXT[o.origin]}</span>
      {o.origin !== 'USER' && <span className={`tag ${tone}`}>{st}</span>}
      {canEdit && o.state === 'PENDING' && field !== 'name' && <>
        <button className="small-action" disabled={busy} onClick={() => onDecide(field, 'CONFIRM')} aria-label={`Confirmar ${field}`}>Confirmar</button>
        <button className="small-action" disabled={busy} onClick={() => onDecide(field, 'REJECT')} aria-label={`Rejeitar ${field}`}>Rejeitar</button>
      </>}
    </span>
  );
}

function Ev({ ids, evs }: { ids: string[]; evs: Map<string, ContextEvidenceDto> }) {
  const list = ids.map((i) => evs.get(i)).filter((e): e is ContextEvidenceDto => !!e);
  if (!list.length) return null;
  return (
    <details className="evidence">
      <summary>Ver {list.length} evidência{list.length > 1 ? 's' : ''}</summary>
      {list.map((e) => (
        <div key={e.id} className="ev">
          <blockquote>{e.excerpt}</blockquote>
          <span className="cell-sub wide">{safeHref(e.url) ? <a href={e.url!} target="_blank" rel="noopener noreferrer">{e.title}</a> : e.title} · {NATURE_TXT[e.nature] ?? e.nature}{e.confidence ? ` · confiança ${e.confidence.toLowerCase()}` : ''} · coletada {when(e.collectedAt)}{e.confirmation === 'CONFIRMADA' ? ' · confirmada' : e.confirmation === 'REJEITADA' ? ' · rejeitada' : ''}</span>
        </div>
      ))}
    </details>
  );
}

function Items({ title, items, evs, empty }: { title: string; items: CompetitorAnalysisItem[] | undefined; evs: Map<string, ContextEvidenceDto>; empty?: string }) {
  if (!items?.length) return empty ? <div className="cmp-block"><h5>{title}</h5><p className="cell-sub">{empty}</p></div> : null;
  return (
    <div className="cmp-block">
      <h5>{title}</h5>
      <ul className="review-list">
        {items.map((x, i) => (
          <li key={i} className={`suggestion n-${x.nature.toLowerCase()}`}>
            <span><span className={`tag ${NATURE_TONE[x.nature] ?? 'gray'}`}>{NATURE_TXT[x.nature]}</span> {x.value} <span className="cell-sub inline">· confiança {x.confidence.toLowerCase()}</span></span>
            {x.note && <span className="cell-sub wide">{x.note}</span>}
            <Ev ids={x.evidenceIds} evs={evs} />
          </li>
        ))}
      </ul>
    </div>
  );
}

const FIELD_TXT: Partial<Record<CompetitorField, string>> = { tradeName: 'Nome comercial', legalName: 'Razão social', website: 'Site', taxId: 'CNPJ', city: 'Cidade', region: 'Estado', country: 'País', description: 'Descrição', reason: 'Justificativa', offeringIds: 'Ofertas relacionadas', aliases: 'Outros nomes', name: 'Nome' };

export function CompetitorReview({ id, ctx, canEdit, onChanged, known }: { id: string; ctx: Ctx; canEdit: boolean; onChanged: () => void; known: CompetitorDetailDto[] }) {
  const [c, setC] = useState<CompetitorFull | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'view' | 'edit' | 'rerun' | 'switch'>('view');
  const [force, setForce] = useState(false);
  const { busy, run } = useBusy();
  const load = useCallback(() => competitorAdapter.get(id).then((r) => { setC(r); setError(null); }).catch((e: Error) => setError(e.message)), [id]);
  useEffect(() => { void load(); }, [load]);
  const st = c ? researchState(c) : null;
  useEffect(() => { if (!st?.active) return; const t = setInterval(load, 3000); return () => clearInterval(t); }, [st?.active, load]);
  const after = (r: unknown) => { if (r) { void load(); onChanged(); } };
  const evs = useMemo(() => new Map((c?.evidences ?? []).map((e) => [e.id, e])), [c?.evidences]);
  if (error) return <p role="alert" className="form-error">{error}</p>;
  if (!c || !st) return <p className="cell-sub wide" role="status"><i className="spin" aria-hidden /> Carregando…</p>;
  const a = c.analysis;
  const r = c.latestRun;
  const offeringName = (oid: string) => ctx.offerings.find((o) => o.id === oid)?.name ?? 'oferta arquivada';
  const classify = (s: CompetitorStatus, msg: string) => run(() => competitorAdapter.classify(c.id, s), msg).then(after);
  const decide = (f: CompetitorField, d: 'CONFIRM' | 'REJECT' | 'ACCEPT_PROPOSAL' | 'REJECT_PROPOSAL') => run(() => competitorAdapter.field(c.id, f, d), d === 'CONFIRM' || d === 'ACCEPT_PROPOSAL' ? 'informação confirmada.' : 'informação rejeitada.').then(after);
  const rerun = (trigger: 'RERUN' | 'RETRY') => run(() => competitorAdapter.research(c.id, { force: trigger === 'RERUN' && force, trigger }, `ui-${newId()}`), 'pesquisa iniciada.').then((x) => { setMode('view'); setForce(false); after(x); });
  const proposals = ((a as unknown as { proposals?: Array<{ field: CompetitorField; value: unknown; current: unknown }> } | null)?.proposals ?? []);
  const show = (f: CompetitorField, v: unknown) => (f === 'offeringIds' ? (v as string[]).map(offeringName).join(', ') : Array.isArray(v) ? v.join(', ') : f === 'taxId' ? fmtCnpj(String(v ?? '')) : String(v ?? '—'));
  const archived = c.status === 'ARCHIVED';
  return (
    <div className="cmp-review">
      {/* Progresso e estado da execução */}
      <div className={`cmp-progress ${st.tone}`} role="status" aria-live="polite">
        {!r ? (
          <p>Esta empresa ainda não foi pesquisada{c.origin === 'ENRICHMENT' || c.origin === 'SITE_ANALYSIS' ? ' (foi sugerida pela pesquisa da sua empresa)' : ''}{c.origin === 'MARKET_ANALYSIS' ? <> (foi descoberta numa {c.sourceAnalysisId ? <Link to={`/mercado/${c.sourceAnalysisId}?aba=concorrentes`}>análise de mercado</Link> : 'análise de mercado'} e incorporada por você)</> : null}.{' '}
            {canEdit && !archived && <button className="cta" disabled={busy} onClick={() => rerun('RERUN')}>Pesquisar esta empresa</button>}</p>
        ) : st.active ? (
          <>
            <ol className="steps-line">{RUN_STEPS.map(([k, label], i) => {
              const cur = RUN_STEPS.findIndex(([x]) => x === r!.stage);
              return <li key={k} className={i < cur ? 'done' : i === cur ? 'now' : ''}>{i === cur && <i className="spin" aria-hidden />}{label}</li>;
            })}</ol>
            <p className="cell-sub wide">Pesquisando {r?.domain ?? c.name} · {r?.sourcesCollected ?? 0} fonte(s) lida(s). Você pode sair desta página; a pesquisa continua.</p>
          </>
        ) : r?.status === 'FAILED' ? (
          <>
            <p><b>Falha na análise.</b> {r.failureReason ?? 'A pesquisa não terminou.'} A empresa selecionada{a ? ' e os resultados anteriores' : ''} foram preservados.</p>
            {canEdit && !archived && <button className="cta" disabled={busy} onClick={() => rerun('RETRY')}>Tentar novamente</button>}
          </>
        ) : (
          <p><span className={`tag ${st.tone}`}>{st.label}</span> {r ? `Versão ${r.version} · ${when(r.finishedAt)} · ${r.sourcesCollected} fonte(s)` : ''}{r?.status === 'PARTIAL' && canEdit && !archived ? <> · <button className="link-btn" disabled={busy} onClick={() => rerun('RETRY')}>Tentar novamente para completar</button></> : null}</p>
        )}
        {r && !st.active && r.notes.length > 0 && <details><summary className="cell-sub">Notas da pesquisa ({r.notes.length})</summary><ul className="notes">{r.notes.map((n) => <li key={n}>{n}</li>)}</ul></details>}
      </div>

      {mode === 'switch' && <AddCompetitor ctx={ctx} switchId={c.id} known={known} onCancel={() => setMode('view')} onOpenExisting={() => setMode('view')} onDone={() => { setMode('view'); after(true); }} />}
      {mode === 'edit' && <EditForm c={c} ctx={ctx} onCancel={() => setMode('view')} onSaved={() => { setMode('view'); after(true); }} />}

      {/* Identidade */}
      <div className="cmp-block">
        <h4>Identidade {c.identityConfidence && <ConfidenceTag c={c.identityConfidence} />}</h4>
        <dl className="kv cmp-kv">
          <dt>Nome</dt><dd>{c.name} <Provenance c={c} field="name" canEdit={canEdit} onDecide={decide} busy={busy} /></dd>
          {(['tradeName', 'legalName', 'website', 'taxId', 'city', 'region', 'country', 'description'] as CompetitorField[]).map((f) => {
            const v = f === 'website' ? c.website : (c as unknown as Record<string, unknown>)[f];
            if ((v === null || v === undefined || v === '') && !c.fieldOrigins[f]) return null;
            return <Fragment key={f}><dt>{FIELD_TXT[f]}</dt><dd>{f === 'website' && safeHref(c.website) ? <a href={c.website!} target="_blank" rel="noopener noreferrer">{c.domain}</a> : v ? show(f, v) : <span className="cell-sub inline">—</span>} <Provenance c={c} field={f} canEdit={canEdit} onDecide={decide} busy={busy} /></dd></Fragment>;
          })}
        </dl>
        {c.identity?.signals && c.identity.signals.length > 0 && (
          <details><summary className="cell-sub">Como a empresa foi identificada (conferido em {when(c.identity.verifiedAt ?? null)})</summary>
            <ul className="signals">{c.identity.signals.map((s) => <li key={s.key} className={`sig ${s.state.toLowerCase()}`}><b>{s.state === 'CONFIRMA' ? '✓' : s.state === 'DIVERGE' ? '✗' : '–'} {s.label}:</b> {s.detail}</li>)}</ul>
            {(c.identity.divergences ?? []).length > 0 && <ul className="notes">{c.identity.divergences!.map((d) => <li key={d}>{d}</li>)}</ul>}
          </details>
        )}
      </div>

      {/* Relação competitiva */}
      {a && (
        <div className="cmp-block">
          <h4>Relação competitiva</h4>
          <p>
            {a.suggestedClassification ? <>Sugestão da pesquisa: <b>{SUGGESTION_TXT[a.suggestedClassification]}</b></> : <b>Sem evidência suficiente para sugerir uma classificação</b>}
            {a.competitionLevel ? ` · grau de concorrência ${a.competitionLevel.toLowerCase()}` : ''} · confiança {a.confidence.toLowerCase()}
            {a.generatedBy === 'REGRAS' ? ' · sem análise por IA' : ''}
          </p>
          {a.summary && <p className="cmp-desc">{a.summary}</p>}
          {a.rationale && <p className="cell-sub wide"><b>Justificativa:</b> {a.rationale}</p>}
          <p className="cell-sub wide">Comparado com {a.comparedWith.contextVersion ? `o contexto confirmado (versão ${a.comparedWith.contextVersion})` : 'o rascunho do contexto (ainda não confirmado)'}: {a.comparedWith.offerings.join(', ') || 'sem ofertas cadastradas'}.</p>
          <div className="overlap-grid">
            {Object.entries(a.overlap).map(([k, o]) => (
              <div key={k} className="ov"><span className="cell-sub">{OVERLAP_TXT[k]}</span><span className={`tag ${LEVEL_TONE[o.level] ?? 'gray'}`}>{o.level === 'DESCONHECIDA' ? 'sem evidência' : o.level.toLowerCase()}</span>{o.note && <span className="cell-sub">{o.note}</span>}<Ev ids={o.evidenceIds} evs={evs} /></div>
            ))}
          </div>
          <div className="two-col">
            <Items title="Evidências favoráveis à concorrência" items={a.favorable} evs={evs} empty="Nenhuma com fonte." />
            <Items title="Evidências contrárias" items={a.against} evs={evs} empty="Nenhuma com fonte." />
          </div>
        </div>
      )}

      {/* Ofertas relacionadas */}
      {a && (
        <div className="cmp-block">
          <h4>Ofertas relacionadas</h4>
          {c.offeringIds.length > 0 && <p className="cell-sub wide">Ligado às suas ofertas: {c.offeringIds.map(offeringName).join(', ')} <Provenance c={c} field="offeringIds" canEdit={canEdit} onDecide={decide} busy={busy} /></p>}
          {a.offerings.length ? (
            <ul className="review-list">
              {a.offerings.map((o, i) => {
                const [rl, rt] = RELATION_TXT[o.relation] ?? [o.relation, 'gray'];
                return (
                  <li key={i} className={`suggestion n-${o.nature.toLowerCase()}`}>
                    <span><b>{o.value}</b> <span className={`tag ${rt}`}>{rl}</span>{o.tenantOfferings.length ? <span className="cell-sub inline"> ↔ {o.tenantOfferings.join(', ')}</span> : null}</span>
                    {o.note && <span className="cell-sub wide">{o.note}</span>}
                    <span className="cell-sub wide">{NATURE_TXT[o.nature]} · confiança {o.confidence.toLowerCase()}</span>
                    <Ev ids={o.evidenceIds} evs={evs} />
                  </li>
                );
              })}
            </ul>
          ) : <p className="cell-sub">Nenhuma oferta identificada nas fontes lidas.</p>}
        </div>
      )}

      {a && (
        <details className="cmp-block">
          <summary><h4 className="inline">Mais sobre a empresa</h4> <span className="cell-sub inline">segmentos, público, posicionamento, clientes, parceiros, tecnologias, presença</span></summary>
          <Items title="Segmentos atendidos" items={a.segments} evs={evs} />
          <Items title="Mercados e regiões" items={a.regions} evs={evs} />
          <Items title="Público-alvo" items={a.audience} evs={evs} />
          <Items title="ICPs aparentes" items={a.apparentIcps} evs={evs} />
          <Items title="Posicionamento" items={a.positioning} evs={evs} />
          <Items title="Proposta de valor" items={a.valueProposition} evs={evs} />
          <Items title="Diferenciais declarados" items={a.differentiators} evs={evs} />
          <Items title="Clientes ou casos públicos" items={a.clients} evs={evs} />
          <Items title="Parceiros" items={a.partners} evs={evs} />
          <Items title="Tecnologias e especializações" items={a.technologies} evs={evs} />
          <Items title="Assuntos recorrentes" items={a.topics} evs={evs} />
          <Items title="Redes sociais oficiais" items={a.socialProfiles} evs={evs} />
          <Items title="Notícias e fontes públicas" items={a.news} evs={evs} />
          {a.gaps.length > 0 && <div className="cmp-block"><h5>Não encontrado</h5><ul className="notes">{a.gaps.map((g) => <li key={g}>{g}</li>)}</ul></div>}
        </details>
      )}

      {/* Mudanças sugeridas pela nova pesquisa (só para o que você informou ou confirmou) */}
      {proposals.length > 0 && (
        <div className="cmp-block">
          <h4>Mudanças sugeridas pela nova pesquisa</h4>
          <p className="cell-sub wide">Estes campos foram informados ou confirmados por você e não foram alterados. Decida se quer trocar.</p>
          <ul className="review-list">
            {proposals.map((p) => (
              <li key={p.field}>
                <span><b>{FIELD_TXT[p.field]}</b>: <span className="cell-sub inline">atual</span> {show(p.field, p.current)} → <span className="cell-sub inline">pesquisa</span> {show(p.field, p.value)}</span>
                {canEdit && <span className="btn-row"><button className="small-action" disabled={busy} onClick={() => decide(p.field, 'ACCEPT_PROPOSAL')}>Usar o valor da pesquisa</button><button className="small-action" disabled={busy} onClick={() => decide(p.field, 'REJECT_PROPOSAL')}>Manter o meu</button></span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Justificativa e observações */}
      <div className="cmp-block">
        <h4>Por que pode ser concorrente</h4>
        <p>{c.reason ?? <span className="cell-sub inline">—</span>} <Provenance c={c} field="reason" canEdit={canEdit} onDecide={decide} busy={busy} /></p>
        <Notes c={c} canEdit={canEdit && !archived} onSaved={() => after(true)} />
      </div>

      {/* Evidências e histórico */}
      <details className="cmp-block">
        <summary><h4 className="inline">Evidências ({c.evidences.length})</h4></summary>
        <ul className="review-list">{c.evidences.slice(0, 60).map((e) => (
          <li key={e.id}><span>{safeHref(e.url) ? <a href={e.url!} target="_blank" rel="noopener noreferrer">{e.title}</a> : e.title}</span><blockquote>{e.excerpt}</blockquote>
            <span className="cell-sub wide">{NATURE_TXT[e.nature] ?? e.nature}{e.confidence ? ` · confiança ${e.confidence.toLowerCase()}` : ''} · coletada {when(e.collectedAt)} · versão {c.runs.find((x) => x.id === e.runId)?.version ?? '—'}</span></li>
        ))}</ul>
      </details>
      <details className="cmp-block">
        <summary><h4 className="inline">Histórico de pesquisas ({c.runs.length})</h4></summary>
        <ul className="review-list">{c.runs.map((x) => (
          <li key={x.id}><span><b>Versão {x.version}</b> · {when(x.createdAt)} · {x.requestedByName ?? '—'} · {TRIGGER_TXT[x.trigger]}{x.force ? ', releitura completa' : ''}</span>
            <span className="cell-sub wide">{({ SUCCEEDED: 'concluída', PARTIAL: 'parcial', FAILED: 'falhou', QUEUED: 'na fila', RUNNING: 'em andamento' } as Record<string, string>)[x.status]} · {x.sourcesCollected} fonte(s) lida(s), {x.sourcesFailed} com falha · {x.llm?.status === 'CALLED' ? `IA: ${x.llm.provider} (${x.llm.model})` : 'sem IA'}{x.durationMs ? ` · ${Math.round(x.durationMs / 1000)} s` : ''}{x.failureReason ? ` · ${x.failureReason}` : ''}</span>
            {x.changes && <span className="cell-sub wide">Atualizado: {x.changes.applied.map((f) => FIELD_TXT[f as CompetitorField] ?? f).join(', ') || 'nada'} · preservado: {x.changes.preserved.map((f) => FIELD_TXT[f as CompetitorField] ?? f).join(', ') || 'nada'}</span>}</li>
        ))}</ul>
      </details>

      {/* Ações */}
      {canEdit && (
        <div className="cmp-actions" role="group" aria-label="Ações sobre o concorrente">
          {archived ? (
            <button className="cta" disabled={busy} onClick={() => classify('HYPOTHESIS', 'concorrente restaurado como hipótese.')}>Restaurar</button>
          ) : mode === 'rerun' ? (
            <div className="confirm-box" role="dialog" aria-labelledby={`rr-${c.id}`}>
              <p id={`rr-${c.id}`}><b>Refazer pesquisa?</b> Uma nova versão será criada; a anterior fica no histórico. O que você informou ou confirmou é preservado — diferenças aparecem como mudanças sugeridas.</p>
              <label className="check"><input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} /> Ler de novo todas as páginas (ignora as lidas nas últimas horas)</label>
              <div className="btn-row"><button className="secondary" onClick={() => setMode('view')}>Cancelar</button><button className="cta" disabled={busy || st.active} onClick={() => rerun('RERUN')}>Refazer pesquisa</button></div>
            </div>
          ) : (
            <>
              <button className="cta" disabled={busy || st.active || c.status === 'CONFIRMED'} onClick={() => classify('CONFIRMED', 'concorrente confirmado.')}>Confirmar concorrente</button>
              <button className="secondary" disabled={busy || st.active} onClick={() => classify('HYPOTHESIS', 'mantido como hipótese.')}>Manter como hipótese</button>
              <button className="secondary" disabled={busy || st.active || c.status === 'RELATED'} onClick={() => classify('RELATED', 'marcado como empresa relacionada.')}>Marcar como empresa relacionada</button>
              <button className="secondary" disabled={busy || st.active || c.status === 'DISCARDED'} onClick={() => classify('DISCARDED', 'descartado.')}>Descartar</button>
              <button className="secondary" disabled={busy} onClick={() => setMode('edit')}>Editar informações</button>
              <button className="secondary" disabled={busy || st.active} onClick={() => setMode('rerun')}>Refazer pesquisa</button>
              <button className="secondary" disabled={busy || st.active} onClick={() => setMode('switch')}>Trocar empresa</button>
              <button className="secondary" disabled={busy || st.active} onClick={() => classify('ARCHIVED', 'concorrente arquivado.')}>Arquivar</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Notes({ c, canEdit, onSaved }: { c: CompetitorDetailDto; canEdit: boolean; onSaved: () => void }) {
  const [v, setV] = useState(c.notes ?? '');
  const { busy, run } = useBusy();
  useEffect(() => setV(c.notes ?? ''), [c.notes]);
  return (
    <form onSubmit={(e) => { e.preventDefault(); void run(() => competitorAdapter.edit(c.id, { notes: v.trim() || null }), 'observações salvas.').then((r) => r && onSaved()); }}>
      <div className="field wide"><label htmlFor={`notes-${c.id}`}>Observações</label><textarea id={`notes-${c.id}`} rows={2} maxLength={2000} value={v} onChange={(e) => setV(e.target.value)} disabled={!canEdit} /></div>
      {canEdit && v !== (c.notes ?? '') && <div className="form-actions"><button className="cta" disabled={busy}>Salvar observações</button></div>}
    </form>
  );
}

function EditForm({ c, ctx, onCancel, onSaved }: { c: CompetitorDetailDto; ctx: Ctx; onCancel: () => void; onSaved: () => void }) {
  const { busy, run } = useBusy();
  const fields: Array<[string, string, 'text' | 'textarea' | 'list']> = [['name', 'Nome', 'text'], ['tradeName', 'Nome comercial', 'text'], ['legalName', 'Razão social', 'text'], ['website', 'Site', 'text'],
    ['city', 'Cidade', 'text'], ['region', 'Estado', 'text'], ['country', 'País', 'text'], ['aliases', 'Outros nomes (um por linha)', 'list'], ['description', 'Descrição', 'textarea'], ['reason', 'Por que pode ser concorrente', 'textarea']];
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const body: Record<string, unknown> = {};
    for (const [k, , kind] of fields) {
      const raw = String(f.get(k) ?? '');
      const cur = (c as unknown as Record<string, unknown>)[k];
      const next = kind === 'list' ? raw.split('\n').map((x) => x.trim()).filter(Boolean) : raw.trim() || null;
      if (JSON.stringify(next ?? null) !== JSON.stringify(kind === 'list' ? cur ?? [] : cur ?? null)) body[k] = next;
    }
    const offs = f.getAll('offeringIds').map(String);
    if (JSON.stringify([...offs].sort()) !== JSON.stringify([...c.offeringIds].sort())) body.offeringIds = offs;
    if (!Object.keys(body).length) { onCancel(); return; }
    void run(() => competitorAdapter.edit(c.id, body), 'informações atualizadas.').then((r) => r && onSaved());
  };
  return (
    <form className="cmp-block" onSubmit={submit}>
      <h4>Editar informações</h4>
      <p className="cell-sub wide">O que você salvar aqui passa a ser informação sua, confirmada — uma nova pesquisa não sobrescreve.</p>
      <div className="form-grid">
        {fields.map(([k, label, kind]) => {
          const v = (c as unknown as Record<string, unknown>)[k];
          return (
            <div key={k} className={`field${kind !== 'text' ? ' wide' : ''}`}>
              <label htmlFor={`e-${c.id}-${k}`}>{label}</label>
              {kind === 'text' ? <input id={`e-${c.id}-${k}`} name={k} defaultValue={String(v ?? '')} required={k === 'name'} minLength={k === 'name' ? 2 : undefined} />
                : <textarea id={`e-${c.id}-${k}`} name={k} rows={kind === 'list' ? 2 : 3} defaultValue={kind === 'list' ? ((v as string[]) ?? []).join('\n') : String(v ?? '')} />}
            </div>
          );
        })}
        <div className="field wide">
          <label htmlFor={`e-${c.id}-off`}>Ofertas relacionadas (suas)</label>
          <select id={`e-${c.id}-off`} name="offeringIds" multiple defaultValue={c.offeringIds}>{ctx.offerings.filter((o) => o.status !== 'ARCHIVED').map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>
        </div>
      </div>
      <div className="form-actions"><button type="button" className="secondary" onClick={onCancel}>Cancelar</button><button className="cta" disabled={busy}>Salvar</button></div>
    </form>
  );
}
